import { DEV_MODE } from "Constant/app/process";

import { TestEventsListType } from "../../../enums/test-events";

/**
 * Dev only stand in for the tracker test-event endpoints, mirroring
 * `FailedLeadsModal.mock` and the Facebook flow in `components/Leads/LeadsStep1`
 * where `DEV_MODE` swaps an empty API response for a locally built one. Real
 * data always wins: these fixtures only fill in when the backend returned
 * nothing, so the Events and Requests tabs have something to render.
 */

const MINUTE_MS = 60 * 1000;

const toEventTime = (date) => date.toISOString().slice(0, 19).replace("T", " ");

const MOCK_PIXEL_ID = "1099238471123456";

/**
 * One fixture per branch of the events table: every status chip (success,
 * processed, error), a row that was received but never sent, and a long event
 * name so the EllipsisText truncation is visible.
 */
const EVENT_FIXTURES = [
    {
        event_name: "PageView",
        status: "success",
        event_received: true,
        event_sent: true,
    },
    {
        event_name: "ViewContent",
        status: "success",
        event_received: true,
        event_sent: true,
    },
    {
        event_name: "AddToCart",
        status: "processed",
        event_received: true,
        event_sent: true,
    },
    {
        event_name: "InitiateCheckout",
        status: "error",
        event_received: true,
        event_sent: false,
    },
    {
        event_name: "Purchase",
        status: "success",
        event_received: true,
        event_sent: true,
    },
    {
        event_name: "CompleteRegistrationWithAVeryLongMockEventName",
        status: "processed",
        event_received: true,
        event_sent: false,
    },
    {
        event_name: "Lead",
        status: "error",
        event_received: true,
        event_sent: false,
    },
];

const REQUEST_FIXTURES = [
    { event_name: "PageView", ip: "192.168.1.24" },
    { event_name: "ViewContent", ip: "10.0.4.117" },
    { event_name: "AddToCart", ip: "2001:0db8:85a3:0000:0000:8a2e:0370:7334" },
    { event_name: "Purchase", ip: "172.16.238.9" },
    { event_name: "Lead", ip: "203.0.113.42" },
];

const MOCK_EVENTS_TOTAL = 23;
const MOCK_REQUESTS_TOTAL = 17;

/**
 * Repeats the fixtures up to `totalCount`, walking the timestamps backwards so
 * the newest row is first, and keeping ids unique so the detail modal opens a
 * distinct event per row.
 */
const buildRows = (fixtures, totalCount, idBase, now) =>
    Array.from({ length: totalCount }, (_, index) => ({
        id: idBase + index,
        ...fixtures[index % fixtures.length],
        event_time: toEventTime(new Date(now - index * 7 * MINUTE_MS)),
        pixel_id: MOCK_PIXEL_ID,
    }));

/**
 * Matches what `getTestEventsOrRequestsAsync` returns: the slice reads
 * `response` and `total_count` off it. `page` is the 0-based skip the API uses.
 */
const buildListResponse = (rows, page, limit) => {
    const safeLimit = limit > 0 ? limit : 10;
    const offset = Math.max(0, page) * safeLimit;

    // Slice rather than clamp, so paging past the end returns an empty page the
    // way the real endpoint would instead of repeating the last one.
    return {
        response: rows.slice(offset, offset + safeLimit),
        total_count: rows.length,
    };
};

export const buildDevMockTestEventsResponse = (page = 0, limit = 10) =>
    buildListResponse(
        buildRows(EVENT_FIXTURES, MOCK_EVENTS_TOTAL, 930000, Date.now()),
        page,
        limit,
    );

export const buildDevMockRequestsResponse = (page = 0, limit = 10) =>
    buildListResponse(
        buildRows(REQUEST_FIXTURES, MOCK_REQUESTS_TOTAL, 940000, Date.now()),
        page,
        limit,
    );

export const buildDevMockTestEventsOrRequestsResponse = (
    listType,
    page = 0,
    limit = 10,
) =>
    listType === TestEventsListType.REQUESTS
        ? buildDevMockRequestsResponse(page, limit)
        : buildDevMockTestEventsResponse(page, limit);

/**
 * Payload for the JSON viewer in the event details modal: nested enough to
 * exercise the expand/collapse tree.
 */
export const buildDevMockTestEventDetail = (eventId) => ({
    id: eventId ?? 930000,
    event_name: "Purchase",
    event_time: toEventTime(new Date()),
    pixel_id: MOCK_PIXEL_ID,
    status: "success",
    source: {
        type: "server",
        url: "https://mock.example.com/checkout/success",
        referrer: "https://mock.example.com/cart",
        user_agent:
            "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/124.0 Safari/537.36",
    },
    user_data: {
        em: "mock:hashed_email_2f8a1c",
        ph: "mock:hashed_phone_7b31de",
        client_ip_address: "192.168.1.24",
        fbc: "fb.1.1700000000000.MockClickId",
        fbp: "fb.1.1700000000000.1234567890",
    },
    custom_data: {
        currency: "USD",
        value: 149.9,
        order_id: "MOCK-ORDER-88213",
        contents: [
            { id: "SKU-114", quantity: 1, item_price: 99.9 },
            { id: "SKU-227", quantity: 2, item_price: 25 },
        ],
    },
    processing_results: {
        received_at: toEventTime(new Date(Date.now() - MINUTE_MS)),
        sent_at: toEventTime(new Date()),
        matched_parameters: ["em", "ph", "fbc", "fbp"],
        warnings: [],
    },
});

/**
 * In DEV_MODE an empty (or failed) list is replaced by the fixtures. Outside
 * DEV_MODE, and whenever the backend actually returned rows, the real payload
 * is passed straight through.
 */
export const withDevMockTestEvents = (payload, buildMock) => {
    if (!DEV_MODE) return payload;
    if (payload?.response?.length) return payload;

    return buildMock();
};

export const withDevMockTestEventDetail = (detail, buildMock) => {
    if (!DEV_MODE) return detail;
    if (detail && Object.keys(detail).length) return detail;

    return buildMock();
};
